// 🌿 API Craft Back Office - Foreign Key Display Component
import React, { useState, useEffect } from 'react';
import { crudApi } from '../services/api';
import { ModelField } from '../types';

interface ForeignKeyDisplayProps {
  field: ModelField;
  value: string | undefined;
}

const ForeignKeyDisplay: React.FC<ForeignKeyDisplayProps> = ({ field, value }) => {
  const [displayText, setDisplayText] = useState<string>('');
  const [fullRecord, setFullRecord] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (!value || !field.reference_model) {
      setDisplayText('—');
      return;
    }

    const fetchReferencedRecord = async () => {
      setIsLoading(true);
      try {
        const record = await crudApi.getRecord(field.reference_model!, value);
        
        // Try to find a good display field
        const displayFields = ['name', 'title', 'description', 'label', 'email'];
        let display = '';
        
        for (const fieldName of displayFields) {
          if (record[fieldName]) {
            display = record[fieldName];
            break;
          }
        }
        
        if (!display) {
          display = `Record ${value.substring(0, 8)}...`; // Fallback to shortened ID
        }
        
        setDisplayText(display);
        setFullRecord(record);
      } catch (error) {
        console.error(`Error fetching FK record for ${field.reference_model}:`, error);
        setDisplayText(value); // Fallback to ID on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferencedRecord();
  }, [value, field.reference_model]);

  if (!value) {
    return <span className="text-sage-400">—</span>;
  }

  if (isLoading) {
    return (
      <span className="text-sage-500 flex items-center">
        <svg className="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading...
      </span>
    );
  }

  return (
    <div className="relative inline-block">
      <span 
        className="text-forest-700 cursor-help underline decoration-dotted"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {displayText}
      </span>
      
      {/* Rich Tooltip */}
      {showTooltip && fullRecord && (
        <div className="absolute z-50 bottom-full left-0 mb-2 w-64 bg-forest-900 text-white p-3 rounded-lg shadow-xl border border-forest-700">
          <div className="text-xs font-medium text-mint-200 mb-2">
            {field.reference_model} Details
          </div>
          <div className="space-y-1 text-xs">
            {Object.entries(fullRecord)
              .filter(([key]) => !['created_at', 'updated_at'].includes(key))
              .map(([key, val]) => (
                <div key={key} className="flex">
                  <span className="text-mint-300 capitalize font-medium w-16 flex-shrink-0">{key}:</span>
                  <span className="text-white break-all">{String(val)}</span>
                </div>
              ))}
          </div>
          
          {/* Arrow pointing down */}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-forest-900"></div>
        </div>
      )}
    </div>
  );
};

export default ForeignKeyDisplay;
